import { useCallback, useEffect, useState } from 'react'
import { authApi, type PasskeySummary } from '../api/auth'
import { ApiError } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { createPasskey, isPasskeySupported } from '../lib/webauthn'

export function SecurityPage() {
  const { refresh } = useAuth()
  const [passkeys, setPasskeys] = useState<PasskeySummary[]>([])
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const [busy, setBusy] = useState(false)
  const supported = isPasskeySupported()

  const load = useCallback(async () => {
    try {
      setPasskeys(await authApi.passkeys())
    } catch {
      setMessage({ kind: 'error', text: 'Kunde inte hämta dina passkeys.' })
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function onRegister() {
    setMessage(null)
    setBusy(true)
    try {
      const options = await authApi.passkeyCreationOptions()
      const credentialJson = await createPasskey(options)
      const name = window.prompt('Namnge din passkey (t.ex. "MacBook Touch ID"):') || undefined
      await authApi.passkeyRegister(credentialJson, name)
      setMessage({ kind: 'success', text: 'Passkey registrerad. Du kan nu logga in med Touch ID.' })
      // Refresha AuthContext så passkeyCount uppdateras — annars ligger
      // "Registrera en passkey"-länken kvar på Översikt-sidan.
      await Promise.all([load(), refresh()])
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setMessage({ kind: 'error', text: 'Passkey-registreringen avbröts.' })
      } else if (err instanceof ApiError) {
        const body = err.body as { error?: string; errors?: string[] }
        setMessage({ kind: 'error', text: body?.errors?.join(' · ') ?? body?.error ?? `Fel ${err.status}` })
      } else {
        setMessage({ kind: 'error', text: 'Passkey-registrering misslyckades.' })
      }
    } finally { setBusy(false) }
  }

  async function onRemove(id: string, name: string) {
    if (!window.confirm(`Ta bort passkey "${name}"?`)) return
    try {
      await authApi.passkeyRemove(id)
      await Promise.all([load(), refresh()])
    } catch {
      setMessage({ kind: 'error', text: 'Kunde inte ta bort passkey.' })
    }
  }

  return (
    <main className="page">
      <h1>Säkerhet</h1>
      <p className="muted">Passkeys är snabbare och säkrare än lösenord.</p>

      {message && (
        <div role="status" className={`banner banner-${message.kind === 'success' ? 'success' : 'error'}`}>
          {message.text}
        </div>
      )}

      {!supported && (
        <div className="banner banner-warn">
          Din webbläsare stödjer inte passkeys. Använd senaste Safari, Chrome eller Firefox.
        </div>
      )}

      <section className="card">
        <div className="platform-head">
          <h2>Dina passkeys</h2>
          <button type="button" className="primary" onClick={onRegister} disabled={!supported || busy}>
            {busy ? 'Registrerar…' : 'Registrera passkey'}
          </button>
        </div>

        {passkeys.length === 0 ? (
          <p className="muted small no-accounts">Ingen passkey registrerad ännu.</p>
        ) : (
          <ul className="account-list">
            {passkeys.map((p) => (
              <li key={p.credentialId} className="account-row">
                <div>
                  <div className="account-name">{p.name || '(namnlös passkey)'}</div>
                  <div className="muted small">Skapad {new Date(p.createdAt).toLocaleString('sv-SE')}</div>
                </div>
                {p.isBackedUp && <span className="badge badge-ok">Backad upp</span>}
                <button type="button" className="link" onClick={() => onRemove(p.credentialId, p.name)}>
                  Ta bort
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  )
}
